import { css, cx } from "@emotion/css";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
type DividerOrientation = "horizontal" | "vertical";
export interface DividerProps {
    /**
     * The orientation of the divider.
     * @remarks The vertical orientation requires a parent container using
     * a flexbox display to be visible.
     */
    orientation?: DividerOrientation;
    /**
     * Whether the divider should be hidden from assistive technologies.
     * @default true
     * @remarks /**
     * If the divider is purely decorative, set `aria-hidden` to true to hide it from assistive technologies.
     * If it serves a semantic purpose—visually separating meaningful sections do not hide and set this to false.
     * Screen readers will announce it as something like “Horizontal Splitter” or “Vertical Splitter,” so consider whether
     * that information adds value for the user.
     */
    ariaHidden?: boolean;
}
/**
 * A divider is a visual separator between content. It can be used to separate content into groups or to visually
 * separate content on a page.
 * @param props - The props for the divider.
 * @param props.orientation - The orientation of the divider.
 * @param props.ariaHidden - Whether the divider should be hidden from assistive technologies.
 * @returns A divider component.
 */
export function Divider({ orientation = "horizontal", ariaHidden = true }: DividerProps) {
    const vertical = orientation === "vertical";
    //We default the divider to show as a flex item when the orientation is vertical. This allows the divider
    //to work in a flexbox layout without the consumer to do anything else. This means that we don't technically
    //support the divider with a vertical orientation outside of a flexbox layout. It should be noted that even
    //if we didn't enable this, a fixed height would be required for the vertical divider to be shown.
    return <hr className={cx(dividerStyles, { [verticalDividerStyles]: vertical })} aria-orientation={vertical ? "vertical" : undefined} aria-hidden={ariaHidden}/>;
}
const verticalDividerStyles = css({
    flexShrink: "0",
    borderWidth: "0px thin 0px 0px",
    alignSelf: "stretch",
    height: "auto",
});
const dividerStyles = css({
    borderWidth: "0px 0px thin",
    borderStyle: "solid",
    borderColor: themeTokens.color.border.primary,
    padding: 0,
    margin: 0,
});
